import {updateUrl} from '@github-ui/history'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link as RouterLink} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBdi} from '@github-ui/safe-html/VerifiedHTML'
import {Link as PrimerLink} from '@primer/react'
import {useCallback} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {issueUrl} from '../../utils/urls'
import type {IssueNode} from './header-state-query-types'
import styles from './StickyHeaderTitle.module.css'

export type StickyHeaderTitleFragment = Pick<IssueNode, 'titleHTML' | 'number' | 'url'> & {
  readonly repository: Pick<NonNullable<IssueNode['repository']>, 'name'> & {
    readonly owner: Pick<NonNullable<NonNullable<IssueNode['repository']>['owner']>, 'login'>
  }
}

type StickyHeaderTitleProps = {
  headerTitleData: StickyHeaderTitleFragment
  scrollToTopOnClick?: boolean
}

export function StickyHeaderTitle({headerTitleData, scrollToTopOnClick}: StickyHeaderTitleProps) {
  const {titleHTML, number, repository} = headerTitleData
  const resolvedIssueUrl = issueUrl({owner: repository.owner.login, repo: repository.name, number})
  const issueNumberLabel = `#${number}`

  const scrollToTop = useCallback(() => {
    if (!scrollToTopOnClick) {
      return
    }
    setTimeout(() => {
      // Clicking the link to #top will scroll to the top of the page, but it will also append "#top" to the URL.
      // We want to prevent that from happening, so we replace the URL right after the scroll happens.
      updateUrl(resolvedIssueUrl)
    }, 10)
  }, [resolvedIssueUrl, scrollToTopOnClick])

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = scrollToTopOnClick
    ? {}
    : getHovercardAttributesForType('issue', {
        owner: repository.owner.login,
        repo: repository.name,
        issueNumber: number,
      })

  return (
    <div className={styles.stickyTitleContainer}>
      <PrimerLink
        as={RouterLink}
        {...hovercardAttrs}
        to={scrollToTopOnClick ? '#top' : resolvedIssueUrl}
        target={scrollToTopOnClick ? undefined : '_blank'}
        onClick={scrollToTop}
        className={styles.stickyTitleLink}
      >
        <VerifiedHTMLBdi
          className="markdown-title"
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
          html={titleHTML as SafeHTMLString}
          data-testid={TEST_IDS.issueTitleSticky}
        />
      </PrimerLink>
      <span className={styles.issueNumberText}>{issueNumberLabel}</span>
    </div>
  )
}
